import React, { useState, useMemo } from "react";
import { Modal, Input, Tabs, Tooltip, Empty } from "antd";
import { searchIcons, iconPacks, getIconByName } from "../../utils/IconMap";

const { TabPane } = Tabs;

/**
 * Icon Picker Component
 * @param {boolean} visible - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onSelect - Selection handler, receives { pack, name }
 * @param {object} selectedIcon - Currently selected icon { pack, name }
 */
function IconPicker({ visible, onClose, onSelect, selectedIcon }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [activePack, setActivePack] = useState("fi");

    // Get filtered icons based on search and active pack
    const filteredIcons = useMemo(() => {
        return searchIcons(searchTerm, searchTerm ? null : activePack);
    }, [searchTerm, activePack]);

    const handleSelect = (icon) => {
        onSelect({ pack: icon.pack, name: icon.name });
        onClose();
        setSearchTerm("");
    };

    return (
        <Modal
            title="Select Icon"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            bodyStyle={{ maxHeight: "60vh", overflow: "hidden" }}
        >
            {/* Search Input */}
            <Input
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="large"
                className="mb-4"
                allowClear
            />

            {/* Pack Tabs */}
            {!searchTerm && (
                <Tabs
                    activeKey={activePack}
                    onChange={setActivePack}
                    size="small"
                    className="mb-2"
                >
                    {Object.entries(iconPacks).map(([key, pack]) => (
                        <TabPane tab={pack.label} key={key} />
                    ))}
                </Tabs>
            )}

            {/* Icons Grid */}
            <div
                className="overflow-y-auto"
                style={{ maxHeight: "calc(60vh - 140px)" }}
            >
                {filteredIcons.length === 0 ? (
                    <Empty description="No icons found" />
                ) : (
                    <div className="grid grid-cols-8 gap-2">
                        {filteredIcons.map((icon) => {
                            const IconComponent = icon.component;
                            const isSelected =
                                selectedIcon?.pack === icon.pack &&
                                selectedIcon?.name === icon.name;

                            return (
                                <Tooltip key={`${icon.pack}-${icon.name}`} title={icon.name}>
                                    <button
                                        onClick={() => handleSelect(icon)}
                                        className={`
                                            p-3 rounded-lg border-2 transition-all duration-200
                                            flex items-center justify-center
                                            hover:border-[#1955EB] hover:bg-blue-50
                                            ${isSelected
                                                ? "border-[#1955EB] bg-blue-50"
                                                : "border-gray-200 bg-white"
                                            }
                                        `}
                                    >
                                        <IconComponent className="text-xl text-gray-700" />
                                    </button>
                                </Tooltip>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Selected Info */}
            {selectedIcon && (
                <div className="mt-4 p-2 bg-gray-50 rounded-lg flex items-center gap-2 text-sm text-gray-600">
                    {(() => {
                        const Icon = getIconByName(selectedIcon);
                        return Icon ? <Icon className="text-lg" /> : null;
                    })()}
                    <span>Selected: <strong>{selectedIcon.name}</strong></span>
                </div>
            )}
        </Modal>
    );
}

export default IconPicker;
